import { useQuery } from "@tanstack/react-query";
import dynamic from "next/dynamic";
import { useTranslations } from "next-intl";
import React, { useEffect, useState } from "react";
import { useInstantSearch } from "react-instantsearch";
import { boolean } from "zod";

import { useBreakpoint } from "~/contexts/breakpoint";
import { useConfiguration } from "~/contexts/configuration";
import { convertProductFromAgoliaToEva, getDiscountedAndOOSProductsByIds } from "~/services/algolia-utils";
import Algolia from "~/types/algolia";
import CommonUtils from "~/utils/common-utils";
import Constants from "~/utils/constants";

import HighlightedText from "../common/highlighted-text";
import SkeletonProductTile from "../common/skeletons/skeleton-product-tile";
import styles from "./no-results.module.scss";
import { searchClient } from "./search-client";

const ProductTile = dynamic(() => import("../products/product-tile"), {
  loading: () => <SkeletonProductTile />,
});

export default function NoResults() {
  const t = useTranslations();
  const { results } = useInstantSearch();
  const { algoliaIndexId, configuration, eva } = useConfiguration();
  const breakpoint = useBreakpoint();
  const [defaultHits, setDefaultHits] = useState<any[]>([]);

  useEffect(() => {
    if (results && results.nbHits === 0) {
      const algoliaSearchConfig = CommonUtils.parseJsonSafely(
        process.env.NEXT_PUBLIC_ALGOLIA_SEARCH_CONFIG!
      ) as Algolia.Configuration;
      const distinct = algoliaSearchConfig?.distinct;

      searchClient
        .initIndex(algoliaIndexId)
        .search("", { hitsPerPage: breakpoint == "desktop" ? 8 : 6, distinct })
        .then(({ hits }) => {
          setDefaultHits(hits);
        });
    }
  }, [algoliaIndexId, breakpoint, results]);

  const { data: discountedAndOOSProducts } = useQuery<Algolia.DiscountedAndOOSProducts>({
    queryKey: ["algoliaSearchAvailabilityAndDiscounts", defaultHits.map((hit) => hit.code)],
    staleTime: Constants.DEFAULT_PLP_STALE_TIME,
    keepPreviousData: true,
    queryFn: async () => {
      return (
        (await getDiscountedAndOOSProductsByIds(
          eva,
          defaultHits.map((hit) => hit.objectID)
        )) ?? {}
      );
    },
    enabled: defaultHits.length > 0,
  });

  if (results && results.nbHits === 0) {
    return (
      <div>
        <p className={styles.noResultsPretitle}>{t("algolia.noResultsPretitle")}</p>
        <div className={styles.defaultHits}>
          {defaultHits.map((item, idx) => (
            <ProductTile
              key={item.objectID}
              product={convertProductFromAgoliaToEva(item, configuration.CurrencyID)}
              idxInList={idx}
              listScope="search"
              noLazy={idx < Constants.NUMBER_IMAGES_LOADED_WITH_PRIORITY_ON_PLP}
              algolia={{ product: item, discountedAndOOSProducts }}
            />
          ))}
        </div>
      </div>
    );
  }
  return null;
}
